import * as React from 'react';
import { HelpCircle, PhoneForwarded, PhoneOff, KeyRound, MessageSquareX, Wrench, Code, X, AlertCircle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PhoneInput } from '@/components/ui/phone-input';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormItem } from '@/components/ui/form';
import { SwitchWithLabel } from '@/components/ui/switch-with-label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { Skeleton } from '@/components/ui/skeleton';
import type { Assistant } from '@/api/assistants/assistants.types';
import { getTools } from '@/api/tools/tools.api';
import type { Tool } from '@/api/tools/tools.types';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';

interface FunctionsTabProps {
  assistant: Omit<Assistant, 'name'>;
  onUpdate: (updates: Partial<Assistant>) => void;
}

export function FunctionsTab({ assistant, onUpdate }: FunctionsTabProps) {
  const [inputValue, setInputValue] = React.useState('');
  const [tools, setTools] = React.useState<Tool[]>([]);
  const [isLoadingTools, setIsLoadingTools] = React.useState(true);
  const toast = useEnhancedToast();

  const form = useForm({
    defaultValues: {
      forwardingPhoneNumber: assistant?.forwardingPhoneNumber || null,
    },
  });

  React.useEffect(() => {
    const fetchTools = async () => {
      setIsLoadingTools(true);
      try {
        const response = await getTools();
        if (response.data) {
          setTools(response.data);
        }
      } catch {
        // Error handling is done via the isLoadingTools state
      }
      setIsLoadingTools(false);
    };

    fetchTools();
  }, []);

  const handleUpdate = (key: keyof Assistant, value: unknown) => {
    if (key === 'endCallPhrases') {
      if (Array.isArray(value) && value.length === 0) {
        value = [];
      }
    }
    if (key === "forwardingPhoneNumber") {
      // If the value is an empty string, set it to null
      if (value === '') {
        // console.log("Setting forwardingPhoneNumber to null");
        value = null;
      }
    }
    onUpdate({
      [key]: value
    });
  };

  const handleToolsUpdate = (toolIds: string[]) => {
    onUpdate({
      model: {
        ...assistant.model,
        toolIds
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const trimmedValue = inputValue.trim();
    
    if (e.key === 'Enter' && trimmedValue) {
      e.preventDefault();
      
      // Check minimum length
      if (trimmedValue.length < 2) {
        toast.error({
          title: "Phrase too short",
          description: "Please enter at least 2 characters",
          icon: AlertCircle
        });
        return;
      }
      
      // Check for duplicates (case insensitive)
      const isDuplicate = assistant?.endCallPhrases?.some(
        phrase => phrase.toLowerCase() === trimmedValue.toLowerCase()
      );
      
      if (isDuplicate) {
        toast.error({
          title: "Duplicate phrase",
          description: "This phrase has already been added",
          icon: AlertCircle
        });
        return;
      }
      
      const newPhrases = [...(assistant?.endCallPhrases || []), trimmedValue];
      handleUpdate('endCallPhrases', newPhrases);
      setInputValue('');
    }
  };

  const removePhrase = (index: number) => {
    const newPhrases = assistant?.endCallPhrases?.filter((_, i) => i !== index) || [];
    handleUpdate('endCallPhrases', newPhrases);
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Left Column - Main Configuration */}
      <div className="col-span-8 space-y-6">
        {/* Tools Card */}
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-muted/20">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Wrench className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 space-y-1">
                <CardTitle>Custom Tools</CardTitle>
                <CardDescription>Connect tools and workflows to your assistant</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">

          {/* Tools */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Code className="h-4 w-4 text-primary" />
              <Label>Available Tools</Label>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Tools allow your assistant to perform actions during calls. Select tools to connect workflows or create custom tools with your backend.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {isLoadingTools ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Combobox
                mode="multiple"
                options={[
                  ...tools.map((tool) => ({
                    label: tool.function?.name || 'Unnamed Tool',
                    value: tool.id,
                    description: tool.id
                  })),
                  ...(Array.isArray(assistant.model.toolIds) 
                    ? assistant.model.toolIds.filter(id => !tools.some(tool => tool.id === id))
                    : []
                  ).map(id => ({
                    label: id,
                    value: id,
                    description: id
                  }))
                ]}
                value={Array.isArray(assistant.model.toolIds) ? assistant.model.toolIds : []}
                onValueChange={handleToolsUpdate}
                placeholder="Select tools..."
                searchPlaceholder="Search tools by name or ID..."
                emptyMessage="No tools found."
              />
            )}
            <p className="text-xs text-muted-foreground">
              Search by name or ID. Add tools from the Tools page to enable actions like data lookup, API calls, and workflow integration.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Phone Settings Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <PhoneForwarded className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>Phone Settings</CardTitle>
              <CardDescription>Configure call forwarding and phone controls</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Forwarding Phone Number */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <PhoneForwarded className="h-4 w-4 text-primary" />
              <Label>Forwarding Phone Number</Label>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>The phone number to forward calls to when transfer is requested.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Form {...form}>
              <FormItem>
                <FormControl>
                  <PhoneInput
                    value={form.watch('forwardingPhoneNumber') || undefined}
                    onChange={(value) => {
                      form.setValue('forwardingPhoneNumber', value || '');
                      handleUpdate('forwardingPhoneNumber', value || '');
                    }}
                    defaultCountry="DE"
                    international
                  />
                </FormControl>
              </FormItem>
            </Form>
            <p className="text-xs text-muted-foreground">
              Leave empty to disable call forwarding capability.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* End Call Phrases Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <MessageSquareX className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>End Call Phrases</CardTitle>
              <CardDescription>Configure phrases that trigger call termination</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MessageSquareX className="h-4 w-4 text-primary" />
              <Label>Trigger Phrases</Label>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Enter phrases that will trigger the bot to end the call when spoken by the user. For example: "goodbye", "end call", "hang up".</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="space-y-3">
              {assistant?.endCallPhrases && assistant.endCallPhrases.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 rounded-md border bg-muted/30">
                  {assistant.endCallPhrases.map((phrase, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1.5 px-2.5 py-1">
                      {phrase}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-destructive transition-colors"
                        onClick={() => removePhrase(index)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
              <Input
                placeholder="Type a phrase and press Enter to add..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-9"
              />
              <p className="text-xs text-muted-foreground">
                Press Enter to add phrases. Click × to remove. Minimum 2 characters required.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* Right Column - Function Controls */}
    <div className="col-span-4 space-y-6">
      {/* Call Controls Card */}
      <Card className="shadow-sm">
        <CardHeader className="border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <PhoneOff className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <CardTitle>Call Controls</CardTitle>
              <CardDescription>Enable advanced call functions</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            {/* Enable End Call Function */}
            <SwitchWithLabel
              icon={PhoneOff}
              label="End Call Function"
              subline="Allow assistant to end the call from its side."
              checked={assistant?.endCallFunctionEnabled || false}
              onCheckedChange={(checked) => handleUpdate('endCallFunctionEnabled', checked)}
            />

            {/* Dial Keypad */}
            <SwitchWithLabel
              icon={KeyRound}
              label="Dial Keypad Function"
              subline="Enable dialing digits on the keypad during calls."
              checked={assistant?.dialKeypadFunctionEnabled || false}
              onCheckedChange={(checked) => handleUpdate('dialKeypadFunctionEnabled', checked)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
  );
}